import { Button, Card, Form, OptionToggleGroup, Page, RamblaActionButtonContent } from 'components';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import ChoiceModal from 'pages/ModulePage/components/ChoiceModal/ChoiceModal';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import * as Ui from './ModulePage.styles';

const MODULE_NAME_MODES = {
  NAME: 'name',
  NO_NAME: 'noName',
};

const isModuleNameValid = (values) =>
  values.pageModuleNameMode === MODULE_NAME_MODES.NO_NAME || Boolean(values.pageModuleName?.trim());

const FormValidityBridge = ({ activeModule, isConfigReadOnly, onValidityChange }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    const isConfigValid = isConfigReadOnly || (activeModule?.isValid(values) ?? false);
    onValidityChange(isModuleNameValid(values) && isConfigValid);
  }, [activeModule, isConfigReadOnly, onValidityChange, values]);

  return null;
};

const isRegionSourcedMediaModule = (module) => {
  if (!['banner', 'video'].includes(module?.module_type)) return false;

  const content = module[module.module_type] || module;
  return content?.source === 'region';
};

const PageModulePage = ({
  moduleTypes,
  onSave,
  initialModuleTypeKey = null,
  existingModule = null,
  isPending = false,
  title,
  description,
  saveLabel,
  breadcrumbs,
  loading = false,
  isEditMode = false,
}) => {
  const { t } = useTranslation('ownerPortal');
  const formRef = useRef(null);

  const [moduleTypeKey, setModuleTypeKey] = useState(initialModuleTypeKey);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && initialModuleTypeKey) {
      setModuleTypeKey(initialModuleTypeKey);
    }
  }, [initialModuleTypeKey, isEditMode]);

  const activeModule = moduleTypeKey ? moduleTypes[moduleTypeKey] : null;
  const ConfigComponent = activeModule?.ConfigComponent;
  const defaultModuleName = activeModule ? t(activeModule.nameTranslationPath).toUpperCase() : '';
  const isSavePending = isPending || isSubmitting;
  const isRegionSourcedContent = isEditMode && isRegionSourcedMediaModule(existingModule);
  const initialValues = useMemo(() => {
    if (!activeModule) {
      return {};
    }

    const configValues =
      existingModule && activeModule.parseApiData
        ? activeModule.parseApiData(existingModule)
        : activeModule.defaultValues || {};

    return {
      ...configValues,
      pageModuleName: existingModule ? existingModule.name || defaultModuleName : defaultModuleName,
      pageModuleNameMode:
        !existingModule ||
        !existingModule.name?.trim() ||
        existingModule.name.trim().toUpperCase() === defaultModuleName ||
        existingModule.name.trim().toUpperCase() === activeModule.moduleName
          ? MODULE_NAME_MODES.NO_NAME
          : MODULE_NAME_MODES.NAME,
    };
  }, [activeModule, defaultModuleName, existingModule]);

  const handleOpenTypeModal = () => {
    setIsTypeModalOpen(true);
  };

  const handleSelectType = (key) => {
    if (key !== moduleTypeKey) {
      const nextModule = moduleTypes[key];
      if (nextModule?.defaultValues && formRef.current) {
        formRef.current.resetForm({
          values: {
            ...nextModule.defaultValues,
            pageModuleName: t(nextModule.nameTranslationPath).toUpperCase(),
            pageModuleNameMode: MODULE_NAME_MODES.NO_NAME,
          },
        });
      }
    }
    setModuleTypeKey(key);
    setIsTypeModalOpen(false);
  };

  const handleSubmit = useCallback(async () => {
    if (!formRef.current || !activeModule) return;
    if (isSavePending) return;

    const { values } = formRef.current;
    const isConfigValid = isRegionSourcedContent || activeModule.isValid(values);
    if (!isModuleNameValid(values) || !isConfigValid) return;

    const valuesToSave = {
      ...values,
      pageModuleName:
        values.pageModuleNameMode === MODULE_NAME_MODES.NO_NAME
          ? defaultModuleName
          : values.pageModuleName,
    };

    setIsSubmitting(true);
    try {
      await onSave(moduleTypeKey, valuesToSave, { skipContentUpdate: isRegionSourcedContent });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    activeModule,
    defaultModuleName,
    isRegionSourcedContent,
    isSavePending,
    moduleTypeKey,
    onSave,
  ]);

  const handleValidityChange = useCallback((valid) => {
    setIsFormValid(valid);
  }, []);

  const moduleTypeEntries = Object.entries(moduleTypes);

  return (
    <Page
      title={title}
      breadcrumbs={breadcrumbs}
      description={description}
      withBackButton={false}
      loading={loading}
      footer={{
        component: (
          <Ui.FooterWrapper>
            <Button
              size="lg"
              variant="dark"
              className="rounded"
              onClick={handleSubmit}
              disabled={!isFormValid || isSavePending}
            >
              <ConditionalSpinner showSpinner={isSavePending}>
                <RamblaActionButtonContent icon="addModule">{saveLabel}</RamblaActionButtonContent>
              </ConditionalSpinner>
            </Button>
          </Ui.FooterWrapper>
        ),
        visible: !!activeModule,
      }}
    >
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={() => {}}
        renderDefaultButtons={false}
      >
        {(formikProps) => {
          formRef.current = formikProps;
          return (
            <Ui.ContentWrapper>
              <FormValidityBridge
                activeModule={activeModule}
                isConfigReadOnly={isRegionSourcedContent}
                onValidityChange={handleValidityChange}
              />
              {(activeModule || !isEditMode) && (
                <Card
                  title={t('pageModules.moduleDetails')}
                  subtitle={t('pageModules.moduleDetailsSubtitle')}
                  className="w-100"
                  style={{ marginTop: 24 }}
                >
                  {!isEditMode && (
                    <Form.GroupHorizontal
                      label={t('pageModules.moduleType')}
                      required
                      style={{ marginTop: 0 }}
                    >
                      <Ui.TypeSelectorCard
                        $interactive
                        role="button"
                        tabIndex={0}
                        onClick={handleOpenTypeModal}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOpenTypeModal();
                          }
                        }}
                      >
                        {activeModule ? (
                          <>
                            <Ui.TypeSelectorIcon>
                              <i className={activeModule.iconClassName} aria-hidden="true" />
                            </Ui.TypeSelectorIcon>
                            <Ui.TypeSelectorInfo>
                              <Ui.TypeSelectorLabel>
                                {t(activeModule.nameTranslationPath)}
                              </Ui.TypeSelectorLabel>
                              <Ui.TypeSelectorDescription>
                                {t(activeModule.descriptionTranslationPath)}
                              </Ui.TypeSelectorDescription>
                            </Ui.TypeSelectorInfo>
                            <Ui.TypeSelectorChangeHint>
                              {t('pageModules.changeType')}
                            </Ui.TypeSelectorChangeHint>
                          </>
                        ) : (
                          <Ui.TypeSelectorPlaceholder>
                            {t('pageModules.selectModuleType')}
                          </Ui.TypeSelectorPlaceholder>
                        )}
                      </Ui.TypeSelectorCard>
                    </Form.GroupHorizontal>
                  )}
                  {activeModule && (
                    <Form.GroupHorizontal
                      label={t('pageModules.moduleName')}
                      style={{ marginTop: isEditMode ? 0 : undefined }}
                    >
                      <OptionToggleGroup
                        value={formikProps.values.pageModuleNameMode}
                        onChange={(value) => formikProps.setFieldValue('pageModuleNameMode', value)}
                        options={[
                          {
                            title: t('pageModules.name'),
                            description: t('pageModules.nameDescription'),
                            value: MODULE_NAME_MODES.NAME,
                          },
                          {
                            title: t('pageModules.noName'),
                            description: t('pageModules.noNameDescription'),
                            value: MODULE_NAME_MODES.NO_NAME,
                          },
                        ]}
                      />
                      {formikProps.values.pageModuleNameMode === MODULE_NAME_MODES.NAME && (
                        <div style={{ marginTop: 16 }}>
                          <Form.Input name="pageModuleName" type="text" />
                        </div>
                      )}
                    </Form.GroupHorizontal>
                  )}
                </Card>
              )}

              {isRegionSourcedContent ? (
                <Card
                  title="This content cannot be edited here"
                  subtitle="This module uses region-oriented content. Only content created in this place can be edited here."
                  className="w-100"
                  style={{ marginTop: 24 }}
                />
              ) : ConfigComponent ? (
                <Card
                  title={t('pageModules.configuration')}
                  subtitle={t('pageModules.configurationSubtitle')}
                  className="w-100"
                  style={{ marginTop: 24 }}
                >
                  <ConfigComponent isEditMode={isEditMode} />
                </Card>
              ) : null}
            </Ui.ContentWrapper>
          );
        }}
      </Form>

      <ChoiceModal
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        title={t('pageModules.selectModuleType')}
        options={moduleTypeEntries.map(([key, module]) => ({
          id: key,
          icon: module.iconClassName,
          title: t(module.nameTranslationPath),
          description: t(module.descriptionTranslationPath),
        }))}
        onSelect={handleSelectType}
        selectedId={moduleTypeKey}
        contentStyle={{ maxWidth: '700px' }}
      />
    </Page>
  );
};

PageModulePage.propTypes = {
  moduleTypes: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  initialModuleTypeKey: PropTypes.string,
  existingModule: PropTypes.object,
  isPending: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  saveLabel: PropTypes.string.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

export default PageModulePage;
